import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useOnClickOutside as useHooksOnClickOutside, useEventListener } from 'usehooks-ts';
import { useConstant } from './use-constant';
const handledEvents = ['mousedown', 'touchstart', 'keyup', 'focusin'];
const OnClickOutsideContext = /*#__PURE__*/createContext(null);

/**
 * @deprecated useOnClickOutside does not need to be placed within an OnClickOutsideProvider. Any instances of OnClickOutsideProvider can be removed.
 */
export const OnClickOutsideProvider = ({
  children
}) => {
  const [listening, setListening] = useState(false);
  const onClickOutsideHandlers = useConstant(() => new Set());
  const contextValue = useMemo(() => {
    function listen(handler) {
      onClickOutsideHandlers.add(handler);
      setListening(true);
      return () => {
        onClickOutsideHandlers.delete(handler);
        if (!onClickOutsideHandlers.size) {
          setListening(false);
        }
      };
    }
    return {
      listen
    };
  }, [onClickOutsideHandlers]);
  useEffect(() => {
    function runOnClickOutsideHandlers(event) {
      onClickOutsideHandlers.forEach(handler => {
        const {
          refs,
          onClickOutside,
          useEscapeKey,
          onEscape
        } = handler;
        function runCallbackIfClickedOutside(element) {
          if (refs.some(ref => {
            var _ref$current;
            return ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.contains(element);
          })) {
            return;
          }
          onClickOutside(event);
        }
        const eventType = event.type;
        switch (eventType) {
          case 'keyup':
            {
              const keyboardEvent = event;
              const {
                key
              } = keyboardEvent;
              if (useEscapeKey && key === 'Escape') {
                onClickOutside(event);
                onEscape === null || onEscape === void 0 ? void 0 : onEscape(keyboardEvent);
              }
              if (key !== 'Tab') {
                return;
              }
              runCallbackIfClickedOutside(document.activeElement);
              return;
            }
          case 'touchstart':
          case 'mousedown':
            {
              runCallbackIfClickedOutside(event.target);
              return;
            }
          case 'focusin':
            return;
          default:
            {
              const unsupported = eventType;
              throw new Error(`${unsupported} is not a supported event type`);
            }
        }
      });
    }
    if (listening) {
      handledEvents.forEach(event => {
        document.addEventListener(event, runOnClickOutsideHandlers);
      });
      return () => {
        handledEvents.forEach(event => {
          document.removeEventListener(event, runOnClickOutsideHandlers);
        });
      };
    }
    return undefined;
  }, [listening, onClickOutsideHandlers]);
  return /*#__PURE__*/React.createElement(OnClickOutsideContext.Provider, {
    value: contextValue
  }, children);
};
const noOp = () => {};
export function useOnClickOutside(refs, onClickOutside, options) {
  const getOptions = () => {
    if (typeof options === 'object') {
      return options;
    }
    return {
      listening: options,
      useEscapeKey: false,
      onEscape: undefined
    };
  };
  const {
    listening,
    onEscape,
    useEscapeKey
  } = getOptions();
  const handler = listening ? onClickOutside : noOp;
  useHooksOnClickOutside(refs, handler, 'mousedown');
  useHooksOnClickOutside(refs, handler, 'touchstart');
  useEventListener('keyup', ev => {
    if (!listening) {
      return;
    }
    if (useEscapeKey && ev.key === 'Escape') {
      onClickOutside(ev);
      onEscape === null || onEscape === void 0 ? void 0 : onEscape();
      return;
    }
    if (ev.key === 'Tab' && (!ev.target || !refs.some(ref => {
      var _ref$current2;
      return ref === null || ref === void 0 ? void 0 : (_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.contains(ev.target);
    }))) {
      onClickOutside(ev);
    }
  });
}